/*
 * Copyright (c) 2024, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import {productUrlBuilder, rebuildPathWithParams} from '@salesforce/retail-react-app/app/utils/url'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {
    GIVES_BACK_CATGEORY_ID,
    KENDRAS_PICK_CATEGORY_ID,
    YR_CATEGORIES_ID,
    BONUS_CHOICE,
    SHIPPING,
    BIRTHDAY_50_OFF,
    BIRTHDAY_25_OFF
} from '../constants'
import {formattedURL} from './url'
import {LOYALTY_BDAY_50, LOYALTY_BDAY_25} from '../constants'

const BIRTHDAY_PROMOTION_IDS = [LOYALTY_BDAY_50, LOYALTY_BDAY_25]
const BIRTHDAY_LABELS = {
    [LOYALTY_BDAY_50]: BIRTHDAY_50_OFF,
    [LOYALTY_BDAY_25]: BIRTHDAY_25_OFF
}
// Tracked verbatim rather than localized — these mirror the en-US badge copy.
const FINAL_SALE_BADGE = 'Final Sale'
const ADD_ON_ITEM_BADGE = 'Add-On Item'

const getColorBarCategory = (CBproductID) => {
    if (window.CBContent) {
        const config = window.CBContent
        if (CBproductID) {
            for (var i = 0; i < config.length; i++) {
                if (config[i].styles) {
                    for (var j = 0; j < config[i].styles.length; j++) {
                        if (config[i].styles[j].productId == CBproductID) {
                            return config[i].category
                        }
                    }
                }
            }
        }
    }
}

export const getColorBarStonesNames = (recipe) => {
    let attr = ''
    let stones = []
    let selectedStones

    for (let ctr in recipe?.custom?.stoneSKUs) { // eslint-disable-line
        attr = 'stone_' + ++ctr + '_render'
        if(recipe.configuration && recipe.configuration.hasOwnProperty(attr)) { // eslint-disable-line
            stones.push(recipe.configuration[attr])
        }
    }

    if (stones.length > 0) {
        selectedStones = stones.join('|')
    }

    return selectedStones
}

/**
 * Get the human-friendly version of the variation values that users have selected.
 * Useful for displaying these values in the UI.
 *
 * @param {Object} variationAttributes - The products variation attributes.
 * @param {Object} values - The variations selected attribute values.
 * @returns {Object} - A key value map of the display name and display value.
 *
 * @example
 * const displayValues = getDisplayVariationValues(
 *     [ { "id": "color", "name": "Colour", "values": [ { "name": "royal", "orderable": true, "value": "JJ5FUXX" } ] } ],
 *     { "color": "JJ5FUXX" }
 * )
 * // returns { "Colour": "royal" }
 */
export const getDisplayVariationValues = (variationAttributes, values = {}) => {
    const returnVal = Object.entries(values).reduce((acc, [id, value]) => {
        const attribute = variationAttributes.find(({id: attributeId}) => attributeId === id)
        const attributeValue = attribute.values.find(
            ({value: attributeValue}) => attributeValue === value
        )
        return {
            ...acc,
            [attribute.name]: attributeValue.name
        }
    }, {})
    return returnVal
}

/**
 * Normalizes data for product sets and product bundles into the same format
 * Useful for operations that apply to both product sets and product bundles
 *
 * @param {Object} product - A product set or product bundle
 * @returns {Object} - returns normalized product if product is a set/bundle, otherwise returns original product
 */
export const normalizeSetBundleProduct = (product) => {
    if (!product?.type?.set && !product?.type?.bundle) return product
    return {
        ...product,
        childProducts: product?.type?.set
            ? product.setProducts?.map((child) => {
                  return {product: child, quantity: null}
              })
            : product.bundledProducts
    }
}

/**
 * Creates an array of product items used to update the child variant selections for a product bundle,
 * usually in an updateItemToBasket/updateItemsToBasket shopper baskets API call
 *
 * @param {Object} bundle - A product that is a bundle.
 * @param {Array} childProductSelections - An array of the bundle child products with a selected variant.
 *      Each item in the array should have the product, variant, and quantity.
 * @returns {Array} - an array of bundle child product items that have the updated variant
 */
export const getUpdateBundleChildArray = (bundle, childProductSelections) => {
    const itemsToBeUpdated = []
    bundle?.bundledProductItems?.forEach((bundleChild) => {
        const childSelection = childProductSelections?.find(
            (childProduct) =>
                childProduct.product?.id === bundleChild.productId ||
                childProduct.product?.itemId === bundleChild.itemId
        )

        // only update the item if the selected variant is different then what's in the current bundle
        if (childSelection && childSelection.variant?.productId !== bundleChild.productId) {
            itemsToBeUpdated.push({
                itemId: bundleChild.itemId,
                productId: childSelection.variant.productId,
                quantity: childSelection.quantity ?? bundleChild.quantity
            })
        }
    })
    return itemsToBeUpdated
}

/**
 * This function extract the price information of a given product
 * If a product is a master,
 *  currentPrice: get the lowest price (including promotional prices) among variants
 *  listPrice: get the list price of the variant that has lowest price (including promotional price)
 *  maxPrice: the max price in tieredPrices of variant that has lowest price
 * @param {object} product - product detail object
 * @param {object} opts - options to pass into the function like intl, quantity, and currency
 */
export const getPriceData = (product, opts = {}, isEditModal = false) => {
    const {quantity = 1} = opts
    const isASet = product?.hitType === 'set' || !!product?.type?.set
    const isMaster =
        product?.hitType === 'master' || product?.hitType === 'product' || !!product?.type?.master
    const isColorbar = product?.productId === 'colorbar' || product?.id === 'colorbar'
    let currentPrice
    let variantWithLowestPrice
    // grab the variant that has the lowest price (including promotional price)
    if (isMaster) {
        variantWithLowestPrice = findLowestPrice(product)
        currentPrice = variantWithLowestPrice?.minPrice
    } else {
        currentPrice = findLowestPrice(product, isEditModal)?.minPrice
    }

    // since the price is the lowest value among price books, each product will have at lease a single item tiered price at quantity 1
    // the highest value of tieredPrices is presumptively the list price
    const tieredPrices = variantWithLowestPrice?.data?.tieredPrices || product?.tieredPrices || []
    const maxTieredPrice = tieredPrices?.length
        ? Math.max(...tieredPrices.map((item) => item.price))
        : undefined
    const highestTieredPrice = tieredPrices.find((tier) => tier.price === maxTieredPrice)
    let listPrice = highestTieredPrice?.price * ((isEditModal ? 1 : product?.quantity) || 1)

    // if a product has tieredPrices, get the tiered that has the higher closest quantity to current quantity
    const filteredTiered = tieredPrices.filter((tiered) => tiered.quantity <= quantity)
    const closestTieredPrice =
        filteredTiered.length &&
        filteredTiered.reduce((prev, curr) => {
            return Math.abs(curr.quantity - quantity) < Math.abs(prev.quantity - quantity)
                ? curr
                : prev
        })
    // For colorbar, if the product is on sale, we want to show the original price as list price,
    // otherwise show the current price as list price, since colorbar doesn't have tieredPrices, we will use product price as list price
    if (isColorbar && currentPrice <= product?.price) {
        listPrice = product?.price || product?.basePrice || listPrice
    }
    return {
        currentPrice,
        listPrice,
        pricePerUnit: product?.pricePerUnit,
        isOnSale: currentPrice < listPrice,
        isASet,
        isMaster,
        // For a product, set price is the lowest price of its children, so the price should be considered a range
        // For a master product, when it has more than 2 variants, we use the lowest priced variant, so it is  considered a range price
        //      but for master that has one variant, it is not considered range
        isRange: (isMaster && product?.variants?.length > 1) || isASet || false,
        // priceMax is for product set
        tieredPrice: closestTieredPrice?.price,
        maxPrice: product?.priceMax || maxTieredPrice
    }
}

/**
 * Product set prices need update when you select products
 * If you haven't selected all products, there is a mix
 * between selected by user (selectedChildProducts) and set products rendered on load
 * This function combines them in order to display accurate pricing
 * @param setProducts
 * @param selectedChildProducts
 */
const mapSetProductsToSelectedProducts = (setProducts, selectedChildProducts) => {
    return Array.from(setProducts).map((product) => {
        if (selectedChildProducts[product.id]) {
            return {...product, ...selectedChildProducts[product.id]?.selectedSku}
        }

        return product
    })
}

export const getSetPriceData = (setProducts, selectedChildProducts) => {
    const base = mapSetProductsToSelectedProducts(setProducts, selectedChildProducts)
    const setProductPrices = Object.keys(base)?.reduce((sum, key) => {
        sum[key] = getPriceData(selectedChildProducts[base[key]?.id]?.selectedSku || base[key])

        return sum
    }, {})

    const setProductPriceKeys = Object.keys(setProductPrices)
    const currentPriceSum = setProductPriceKeys.reduce((sum, key) => {
        sum += (setProductPrices[key]?.selectedSku || setProductPrices[key]).currentPrice
        return sum
    }, 0)
    const listPriceSum = setProductPriceKeys.reduce((sum, key) => {
        sum += (setProductPrices[key]?.selectedSku || setProductPrices[key]).listPrice

        return sum
    }, 0)

    return {
        isASet: true,
        listPrice: listPriceSum,
        currentPrice: currentPriceSum,
        isOnSale: currentPriceSum < listPriceSum
    }
}

/**
 * Filters the provided product `ImageGroups` array with the optional filters.
 *
 * @param {ImageGroup[]} imageGroups
 * @param {object} filters
 * @param {string} filters.viewType
 * @param {object} filters.variationValues
 *
 * @returns {ImageGroup[]} filteredImageGroups
 */
export const filterImageGroups = (imageGroups = [], filters) => {
    if (!filters) {
        throw new Error(`Missing required "filters" argument`)
    }

    const {viewType, variationValues = {}} = filters
    // NOTE: For future feature enhancement compatibility we use an object for the `variationValues` as
    // we might want to filter on more than the one attribute id/value pair.
    const [stoneColorId, stoneColorValue] =
        Object.entries(variationValues).find(([id]) => id === 'stoneColor') || []
    const [metalId, metalValue] =
        Object.entries(variationValues).find(([id]) => id === 'metal') || []

    const [letterId, letterValue] =
        Object.entries(variationValues).find(([id]) => id === 'letter') || []

    // Filters
    const typeFilter = viewType ? (group) => group.viewType === viewType : () => true

    // If neither stoneColor nor metal are present, fall back to the previous code
    if (!stoneColorId && !metalId) {
        const [selectedAttributeId, selectedAttributeValue] =
            Object.entries(variationValues)[0] || []

        const attributeFilter =
            !!selectedAttributeId && !!selectedAttributeValue
                ? ({variationAttributes = []}) =>
                      variationAttributes.some(
                          ({id, values}) =>
                              id === selectedAttributeId &&
                              !!values.find(({value}) => value === selectedAttributeValue)
                      )
                : () => true

        return imageGroups?.filter(typeFilter)?.filter(attributeFilter)
    }

    const attributeFilter = ({variationAttributes = []}) => {
        const stoneColorFilter =
            stoneColorId && stoneColorValue
                ? variationAttributes.some(
                      ({id, values}) =>
                          id === stoneColorId && values.some(({value}) => value === stoneColorValue)
                  )
                : false

        const metalFilter =
            metalId && metalValue
                ? variationAttributes.some(
                      ({id, values}) =>
                          id === metalId && values.some(({value}) => value === metalValue)
                  )
                : false

        const letterFilter =
            letterId && letterValue
                ? variationAttributes.some(
                      ({id, values}) =>
                          id === letterId && values.some(({value}) => value === letterValue)
                  )
                : false
        // If stoneColor is found, prioritize that, else check for metal.
        return stoneColorFilter || metalFilter || letterFilter
    }

    return imageGroups?.filter(typeFilter)?.filter(attributeFilter)
}

/**
 * Provided a product this function will return the variation attibutes decorated with
 * `href` and `swatch` image for the given attribute values. This allows easier access
 * when creating components that commonly use this information.
 *
 * @param {Product} product
 * @param {object} opts
 * @param {string} opts.swatchViewType - The `viewTypeId` for the swatch image. Defaults to `swatch`.
 *
 * @returns {VariationAttributes[]} decoratedVariationAttributes
 */
export const getDecoratedVariationAttributes = (product, opts = {}) => {
    const swatchViewType = opts.swatchViewType || 'swatch'

    return product?.variationAttributes?.map((variationAttribute) => ({
        ...variationAttribute,
        values: variationAttribute?.values?.map((value) => {
            const variationValues = {[variationAttribute.id]: value.value}
            const filteredImage = filterImageGroups(product.imageGroups, {
                viewType: swatchViewType,
                variationValues
            })?.[0]?.images[0]
            const defaultImage = filterImageGroups(product.imageGroups, {
                viewType: swatchViewType
            })?.[0]?.images[0]

            return {
                ...value,
                swatch: filteredImage ? filteredImage : defaultImage, // Please return the default image if there is no variationValues available
                href: rebuildPathWithParams(
                    productUrlBuilder({id: product.productId}),
                    variationValues
                )
            }
        })
    }))
}

/**
 * Find the lowest price of a product, across all of its price books and promotions
 * @param {Object} product - product data from the API
 * @returns {Object|undefined} the lowest price for the given product. If it's a promotional price, the promotion will also be returned.
 */
export const findLowestPrice = (product, isEditModal = false) => {
    if (!product) return

    // Look at all of the variants, only if it's a master product.
    // i.e. when a shopper has narrowed down to a variant, do not look into other variants
    const isMaster =
        product?.hitType === 'master' || product?.hitType === 'product' || !!product?.type?.master
    if (isMaster && !product.variants) {
        console.warn(
            'Expecting `product.variants` to exist. For more accuracy, please tweak your API request to ask for the variants details.'
        )
    }
    const array = isMaster && product.variants ? product.variants : [product]

    if (product?.productId === 'colorbar') {
        if (product.price > product.priceAfterItemDiscount) {
            return {minPrice: product.priceAfterItemDiscount, promotion: null, data: null}
        }

        return {minPrice: product.price, promotion: null, data: null}
    }

    const res = array.reduce(
        (prev, data) => {
            let appliedPromos = []
            // for birthday promotion, calculate price only if birthday promo is applied
            data?.productPromotions?.forEach((promo) => {
                if (
                    promo.promotionId !== LOYALTY_BDAY_50 &&
                    promo.promotionId !== LOYALTY_BDAY_25
                ) {
                    appliedPromos.push(promo)
                } else {
                    if (data.c_birthdayCouponApplied) {
                        appliedPromos.push(promo)
                    }
                    // This condition is added because the birthday promotion price should be displayed everywhere except in order summary line items and cart page line items.
                    if (
                        (promo.promotionId === LOYALTY_BDAY_50 ||
                            promo.promotionId === LOYALTY_BDAY_25) &&
                        !data?.priceAdjustments?.length
                    ) {
                        appliedPromos.push(promo)
                    }
                }
            })

            const [smallestPromotionalPrice, promo] = getSmallestValByProperty(
                appliedPromos,
                'promotionalPrice'
            )

            let salePrice = data?.price
            let promotion = null
            if (smallestPromotionalPrice && smallestPromotionalPrice < data?.price) {
                salePrice = smallestPromotionalPrice * ((isEditModal ? 1 : data?.quantity) || 1)
                /**
                 * Birthday promotions are just for 1 quantity, so we should not multiply
                 * smallestPromotionalPrice with quantity in case of these promotions
                 */
                salePrice =
                    promo?.promotionId === LOYALTY_BDAY_50 || promo?.promotionId === LOYALTY_BDAY_25
                        ? product?.priceAfterItemDiscount || smallestPromotionalPrice
                        : smallestPromotionalPrice * ((isEditModal ? 1 : data?.quantity) || 1)
                promotion = promo
            }

            return salePrice < prev.minPrice ? {minPrice: salePrice, promotion, data} : prev
        },
        {minPrice: Infinity, promotion: null, data: null}
    )

    // item discount can also impact the price and has lower value
    if (
        res.minPrice !== Infinity &&
        (product?.priceAfterItemDiscount || product?.priceAfterItemDiscount === 0)
    ) {
        res.minPrice = product.priceAfterItemDiscount
    }
    return {
        ...res,
        // when minPrice is infinity, meaning there is no variant with lowest price found, we rest the value to 0 as minPrice to be able to use this value on UI
        minPrice: res.minPrice === Infinity ? 0 : res.minPrice
    }
}

/**
 * @private
 * Find the smallest value by key from a given array
 * @returns {Array} an array of such smallest value and the item containing this value
 * @example
 * const [value, itemContainingValue] = getSmallestValByProperty(array, key)
 */
const getSmallestValByProperty = (arr, key) => {
    if (!arr || !arr.length) return []
    if (!key) {
        throw new Error('Please specify a key.')
    }
    const filtered = arr.filter((item) => {
        const value = item[key]
        return value === 0 || value === '0' ? true : Number(value)
    })
    if (filtered.length === 0) return []

    return filtered.reduce(
        (prev, item) => {
            const value = item[key]
            const [prevValue] = prev
            return value < prevValue ? [value, item] : prev
        },
        [Infinity, null]
    )
}

export const isInViewport = ({current}) => {
    let available = false
    const currentTop = current.getBoundingClientRect().top + window.scrollY
    const currentBottom = currentTop + current.offsetHeight
    const viewportTop = window.scrollY
    const viewportBottom = viewportTop + window.innerHeight

    if (
        currentBottom > viewportTop &&
        currentTop < viewportBottom &&
        !current.classList.contains('impressedproduct')
    ) {
        available = true
    }
    return available
}

export const formatCategoryTree = (tree) =>
    tree
        .slice(tree.findIndex((i) => i.id === YR_CATEGORIES_ID) + 1)
        .map((i) => i.name)
        .join('/') || ''

export const formatDate = (effectiveDateTime) => {
    let date = effectiveDateTime ? new Date(effectiveDateTime) : new Date()
    date.setMinutes(0)
    date.setSeconds(0)
    if (typeof date === 'object') {
        date = date.toISOString()
    }
    const formattedDate = date.replace(/[-:T.Z]/g, '').slice(0, 12) // YYYYMMDDHHMM
    return formattedDate
}

export const filterImageBadges = (product, category) => {
    let badges = product?.c_imageBadges
    if (category?.id) {
        const categoryId = category.id.toLowerCase()
        badges = badges?.filter((b) => {
            const bLower = b?.toLowerCase() ?? ''

            // Always include these badge types regardless of category
            if (bLower.includes('text-value') || bLower.includes('xx-value')) return true

            // Suppress category-redundant badges
            if (
                bLower.includes('best-seller') &&
                (categoryId.includes('sellers') ||
                    category.parentCategoryTree?.some((p) =>
                        p?.id?.toLowerCase()?.includes('sellers')
                    ))
            )
                return false

            if (
                bLower.includes('new-arrival') &&
                (categoryId.includes('arrivals') ||
                    category.parentCategoryTree?.some((p) => p?.id?.toLowerCase()?.includes('new')))
            )
                return false

            if (bLower.includes('gives-back') && categoryId === GIVES_BACK_CATGEORY_ID) return false

            if (bLower.includes('kendras-pick') && categoryId === KENDRAS_PICK_CATEGORY_ID)
                return false

            return true
        })
    }
    return badges
}

// Badge display names live only inside the SFCC HTML (data-badge-name="…").
export const getBadgeName = (badge) => {
    if (typeof badge !== 'string') return undefined
    return (
        badge.match(/data-badge-name="([^"]+)"/)?.[1] ||
        badge.replace(/<[^>]*>/g, '').trim() ||
        undefined
    )
}

const normalizeBadgeName = (name = '') => name.toLowerCase().replace(/[^a-z0-9]/g, '')

export const getBadgeUrl = (badge, badgeUrlConfig = {}) => {
    const badgeName = normalizeBadgeName(getBadgeName(badge))

    return Object.entries(badgeUrlConfig).find(
        ([name]) => normalizeBadgeName(name) === badgeName
    )?.[1]
}

export const selectPlpImageBadges = (product, category) => {
    const badges = filterImageBadges(product, category)
    const textValueBadge = badges?.find((b) => b?.toLowerCase()?.includes('text-value'))
    const xxValueBadge = badges?.find((b) => b?.toLowerCase()?.includes('xx-value'))
    const xxValue = product?.c_xxValue || product?.representedProduct?.c_xxValue || ''
    const priorityBadge = textValueBadge ?? (xxValueBadge && xxValue ? xxValueBadge : null)
    // badges.jsx slices to 2 before rendering
    return priorityBadge ? [priorityBadge] : badges?.slice(0, 2)
}

export const selectVisiblePromotions = (product, selectedVariant, isBloomReachSearch = false) => {
    const visiblePromos = []
    const excludedPromoIds = new Set()
    const variantPromotions = selectedVariant ? selectedVariant.productPromotions || [] : []
    for (const promo of product?.c_promotions || []) {
        const isVariantPromo = variantPromotions?.find(
            (variantPromo) => variantPromo.promotionId == promo.id
        )
        if (
            !promo?.name ||
            excludedPromoIds.has(promo.id) ||
            (!isVariantPromo && !isBloomReachSearch)
        )
            continue
        visiblePromos.push(promo)
        promo.mutuallyExclusivePromotions?.forEach((id) => excludedPromoIds.add(id))
        if (visiblePromos.length === 2) break
    }
    return visiblePromos
}

// selectVisiblePromotions drops any promo that isn't on the selected variant unless the PLP is
// Bloomreach-driven. The tile gets that flag as a prop; analytics call sites are too far from the
// PLP to thread it, so read the same site pref the PLP reads (config/default.js -> bloomreach).
let bloomreachSearchEnabled
export const isBloomreachSearchEnabled = () => {
    if (bloomreachSearchEnabled !== undefined) return bloomreachSearchEnabled
    try {
        const {config} = getConfig()?.app?.commerceAPI?.bloomreach || {}
        bloomreachSearchEnabled = !!JSON.parse(config)?.enableBloomreachSearch
    } catch (e) {
        bloomreachSearchEnabled = false
    }
    return bloomreachSearchEnabled
}

// The GTM item_badge param: promotional badges only — the callouts rendered
export const getItemBadge = (product, opts = {}) => {
    const {selectedVariant, isBloomReachSearch} = opts
    const names = selectVisiblePromotions(
        product,
        selectedVariant ?? product?.variants?.[0],
        isBloomReachSearch ?? isBloomreachSearchEnabled()
    )
        .map((promo) => getBadgeName(promo?.name))
        .filter(Boolean)
    return names.length ? names.join(',') : null
}

export const selectVisiblePriceAdjustments = (lineItem) =>
    (lineItem?.priceAdjustments ?? []).filter(
        ({appliedDiscount, promotionId}) =>
            appliedDiscount?.type !== BONUS_CHOICE && !BIRTHDAY_PROMOTION_IDS.includes(promotionId)
    )

export const selectBirthdayPriceAdjustments = (lineItem) =>
    (lineItem?.priceAdjustments ?? []).filter(
        ({appliedDiscount, promotionId}) =>
            appliedDiscount?.type !== BONUS_CHOICE && BIRTHDAY_PROMOTION_IDS.includes(promotionId)
    )

export const selectShippingPromotions = (lineItem) =>
    lineItem?.c_promotions?.filter((promotion) => promotion.promotionClass === SHIPPING) || []

export const showFinalSaleBadge = (lineItem) =>
    !!lineItem &&
    (!lineItem?.c_isReturnable ||
        lineItem?.c_isEngravable ||
        lineItem?.c_isExcludedFromSiteWidePromotions) &&
    (!lineItem?.c_isReturnable || lineItem?.c_isEngravable)

export const getLineItemBadge = (lineItem) => {
    const names = [
        // Same label resolution as item-badges.jsx renderAdjustmentLabel: itemText when present,
        // 'Discount' only when there's no applied discount to describe.
        ...selectVisiblePriceAdjustments(lineItem).map(
            (adjustment) =>
                adjustment?.itemText || (adjustment?.appliedDiscount ? undefined : 'Discount')
        ),
        ...(showFinalSaleBadge(lineItem) ? [FINAL_SALE_BADGE] : []),
        ...selectShippingPromotions(lineItem).map((promotion) => promotion?.name),
        ...selectBirthdayPriceAdjustments(lineItem).map(
            (adjustment) => BIRTHDAY_LABELS[adjustment?.promotionId]
        ),
        ...(lineItem?.bonusProductLineItem ? [ADD_ON_ITEM_BADGE] : [])
    ]
        .map(getBadgeName)
        // "xx-value" is the badge type slug, not a label — the amount from c_xxValue is what the
        // tile shows, so it carries no name worth tracking.
        .filter((name) => name && name.toLowerCase() !== 'xx-value')
    return names.length ? names.join(',') : null
}

/** Per-unit price for a basket line item, derived from post-discount line totals when present. */
export const getCartLineUnitPrice = (cartItem) => {
    if (!cartItem) {
        return undefined
    }
    if (cartItem.c_giftCard) {
        return cartItem.amount
    }
    const quantity = cartItem.quantity || 1
    if (cartItem.priceAfterItemDiscount != null) {
        return Number((cartItem.priceAfterItemDiscount / quantity).toFixed(2))
    }
    if (cartItem.price != null) {
        return Number(cartItem.price)
    }
    return undefined
}

// AC: discount is always a number, 0 when the product isn't discounted.
export const getDiscount = (priceData, quantity = 1) => {
    const listPrice = Number(priceData?.listPrice)
    const currentPrice = Number(priceData?.currentPrice)
    if (!Number.isFinite(listPrice) || !Number.isFinite(currentPrice)) return 0
    return Math.max(0, Math.round(((listPrice - currentPrice) / quantity) * 100) / 100)
}

export const getSource = () => {
    let source /** Product Source **/
    if (document?.querySelector('.sf-product-list-page')) {
        source = 'plp'
    } else if (document?.querySelector('.sf-product-detail-page')) {
        source = 'pdp'
    } else if (document?.querySelector('.content-page')) {
        source = 'esp'
    } else if (document?.querySelector('.sf-cart-container')) {
        source = 'cart'
    } else {
        source = 'home'
    }
    return source
}

export const getColorBarConfigs = (recipe, apiKey, demandwareProductID, customerId) => {
    // Initialize Arrays
    let prodArr = []
    let stoneArr = []
    let fcMeta = {}

    let quantity = recipe.product_quantities.product
    let productSku = ''
    if (recipe.customer_id === customerId) {
        productSku =
            recipe.extended_attributes.select_style &&
            recipe.extended_attributes.select_style.value &&
            recipe.extended_attributes.select_style.value.info &&
            recipe.extended_attributes.select_style.value.info.vendorId
                ? recipe.extended_attributes.select_style.value.info.vendorId
                : recipe.product.id
    } else {
        productSku = recipe.product.name ? recipe.product.name : recipe.product.id
    }
    let productName =
        'custom' in recipe && 'productName' in recipe.custom
            ? recipe.custom.productName
            : 'Color Bar'
    let metalSku = ''
    let fluidConfigureProductID = ''

    // Check if uploaded Photo is configured (feature available for Frames)
    let photoAttrs = ['4x6portrait', '4x6landscape', '5x7landscape', '5x7portrait', '4x4']
    let hasPhoto = false

    for (let attr in photoAttrs) { // eslint-disable-line
        if (attr in recipe.configuration && recipe.configuration[attr] !== '') {
            hasPhoto = true
            break
        }
    }
    // No Stone Comparison Value
    let noStone = 'No Stone'

    // Stone Data Extraction
    let stoneSku
    let stoneSku2
    let stoneSku3
    let stoneSku4
    let stoneSku5
    let stoneSku6
    let stoneSku7
    let stoneSku8
    let stoneSku9
    let stoneSku10
    let stoneSku11
    let stoneSku12
    let stoneSku13
    let stoneSku14
    let stoneSku15
    if ('select_metal' in recipe.extended_attributes) {
        // HOME-COLORBAR
        prodArr.push(recipe.extended_attributes.select_metal.value.info.vendorId)
        if(recipe.extended_attributes.select_stone.value.hasOwnProperty('info')) { // eslint-disable-line
            prodArr.push(recipe.extended_attributes.select_stone.value.info.vendorId)
        }
        metalSku = recipe.extended_attributes.select_metal.value.info.vendorId
        if(recipe.extended_attributes.select_stone.value.hasOwnProperty('info')) { // eslint-disable-line
            stoneArr.push(recipe.extended_attributes.select_stone.value.info.vendorId)
        }
    } else if (recipe.customer_id === customerId) {
        // STANDARD Customer ID
        // Metal Data Extraction
        metalSku = recipe.extended_attributes.style_metal_sku.value.info.vendorId

        // Stone Data Extraction
        stoneSku = recipe.extended_attributes.stone_1_sku.value.info.vendorId
        stoneSku2 = recipe.extended_attributes.stone_2_sku.value.info.vendorId
        stoneSku3 = recipe.extended_attributes.stone_3_sku.value.info.vendorId
        stoneSku4 = recipe.extended_attributes.stone_4_sku.value.info.vendorId
        stoneSku5 = recipe.extended_attributes.stone_5_sku.value.info.vendorId
        stoneSku6 = recipe.extended_attributes.stone_6_sku.value.info.vendorId
        stoneSku7 = recipe.extended_attributes.stone_7_sku.value.info.vendorId
        stoneSku8 = recipe.extended_attributes.stone_8_sku.value.info.vendorId
        stoneSku9 = recipe.extended_attributes.stone_9_sku.value.info.vendorId
        stoneSku10 = recipe.extended_attributes.stone_10_sku.value.info.vendorId
        stoneSku11 = recipe.extended_attributes.stone_11_sku.value.info.vendorId
        stoneSku12 = recipe.extended_attributes.stone_12_sku.value.info.vendorId
        stoneSku13 = recipe.extended_attributes.stone_13_sku.value.info.vendorId
        stoneSku14 = recipe.extended_attributes.hasOwnProperty('stone_14_sku') ? recipe.extended_attributes.stone_14_sku.value.info.vendorId : noStone; // eslint-disable-line
        stoneSku15 = recipe.extended_attributes.hasOwnProperty('stone_15_sku') ? recipe.extended_attributes.stone_15_sku.value.info.vendorId : noStone; // eslint-disable-line

        // Dependency Map
        prodArr = [productSku, metalSku, stoneSku]
        stoneSku2 !== noStone ? prodArr.push(stoneSku2) : ''
        stoneSku3 !== noStone ? prodArr.push(stoneSku3) : ''
        stoneSku4 !== noStone ? prodArr.push(stoneSku4) : ''
        stoneSku5 !== noStone ? prodArr.push(stoneSku5) : ''
        stoneSku6 !== noStone ? prodArr.push(stoneSku6) : ''
        stoneSku7 !== noStone ? prodArr.push(stoneSku7) : ''
        stoneSku8 !== noStone ? prodArr.push(stoneSku8) : ''
        stoneSku9 !== noStone ? prodArr.push(stoneSku9) : ''
        stoneSku10 !== noStone ? prodArr.push(stoneSku10) : ''
        stoneSku11 !== noStone ? prodArr.push(stoneSku11) : ''
        stoneSku12 !== noStone ? prodArr.push(stoneSku12) : ''
        stoneSku13 !== noStone ? prodArr.push(stoneSku13) : ''
        stoneSku14 !== noStone ? prodArr.push(stoneSku14) : ''
        stoneSku15 !== noStone ? prodArr.push(stoneSku15) : ''

        // Stone Map
        stoneArr = [stoneSku]
        stoneSku2 !== noStone ? stoneArr.push(stoneSku2) : ''
        stoneSku3 !== noStone ? stoneArr.push(stoneSku3) : ''
        stoneSku4 !== noStone ? stoneArr.push(stoneSku4) : ''
        stoneSku5 !== noStone ? stoneArr.push(stoneSku5) : ''
        stoneSku6 !== noStone ? stoneArr.push(stoneSku6) : ''
        stoneSku7 !== noStone ? stoneArr.push(stoneSku7) : ''
        stoneSku8 !== noStone ? stoneArr.push(stoneSku8) : ''
        stoneSku9 !== noStone ? stoneArr.push(stoneSku9) : ''
        stoneSku10 !== noStone ? stoneArr.push(stoneSku10) : ''
        stoneSku11 !== noStone ? stoneArr.push(stoneSku11) : ''
        stoneSku12 !== noStone ? stoneArr.push(stoneSku12) : ''
        stoneSku13 !== noStone ? stoneArr.push(stoneSku13) : ''
        stoneSku14 !== noStone ? stoneArr.push(stoneSku14) : ''
        stoneSku15 !== noStone ? stoneArr.push(stoneSku15) : ''
    } else {
        fluidConfigureProductID = recipe.product.id
        // Metal Data Extraction
        try {
            metalSku = recipe.extended_attributes.size.value.info.valueUsageVendorId
        } catch (e) {
            metalSku = recipe.extended_attributes.base_sku.value.info.valueUsageVendorId
        }

        // Stone Data Extraction
        stoneSku = recipe.extended_attributes.hasOwnProperty('stone_1_sku') ? recipe.extended_attributes.stone_1_sku.value.info.valueUsageVendorId : noStone; // eslint-disable-line
        stoneSku2 = recipe.extended_attributes.hasOwnProperty('stone_2_sku') ? recipe.extended_attributes.stone_2_sku.value.info.valueUsageVendorId : noStone; // eslint-disable-line
        stoneSku3 = recipe.extended_attributes.hasOwnProperty('stone_3_sku') ? recipe.extended_attributes.stone_3_sku.value.info.valueUsageVendorId : noStone; // eslint-disable-line
        stoneSku4 = recipe.extended_attributes.hasOwnProperty('stone_4_sku') ? recipe.extended_attributes.stone_4_sku.value.info.valueUsageVendorId : noStone; // eslint-disable-line
        stoneSku5 = recipe.extended_attributes.hasOwnProperty('stone_5_sku') ? recipe.extended_attributes.stone_5_sku.value.info.valueUsageVendorId : noStone; // eslint-disable-line
        stoneSku6 = recipe.extended_attributes.hasOwnProperty('stone_6_sku') ? recipe.extended_attributes.stone_6_sku.value.info.valueUsageVendorId : noStone; // eslint-disable-line
        stoneSku7 = recipe.extended_attributes.hasOwnProperty('stone_7_sku') ? recipe.extended_attributes.stone_7_sku.value.info.valueUsageVendorId : noStone; // eslint-disable-line
        stoneSku8 = recipe.extended_attributes.hasOwnProperty('stone_8_sku') ? recipe.extended_attributes.stone_8_sku.value.info.valueUsageVendorId : noStone; // eslint-disable-line
        stoneSku9 = recipe.extended_attributes.hasOwnProperty('stone_9_sku') ? recipe.extended_attributes.stone_9_sku.value.info.valueUsageVendorId : noStone; // eslint-disable-line
        stoneSku10 = recipe.extended_attributes.hasOwnProperty('stone_10_sku') ? recipe.extended_attributes.stone_10_sku.value.info.valueUsageVendorId : noStone; // eslint-disable-line
        stoneSku11 = recipe.extended_attributes.hasOwnProperty('stone_11_sku') ? recipe.extended_attributes.stone_11_sku.value.info.valueUsageVendorId : noStone; // eslint-disable-line
        stoneSku12 = recipe.extended_attributes.hasOwnProperty('stone_12_sku') ? recipe.extended_attributes.stone_12_sku.value.info.valueUsageVendorId : noStone; // eslint-disable-line
        stoneSku13 = recipe.extended_attributes.hasOwnProperty('stone_13_sku') ? recipe.extended_attributes.stone_13_sku.value.info.valueUsageVendorId : noStone; // eslint-disable-line
        stoneSku14 = recipe.extended_attributes.hasOwnProperty('stone_14_sku') ? recipe.extended_attributes.stone_14_sku.value.info.valueUsageVendorId : noStone; // eslint-disable-line
        stoneSku15 = recipe.extended_attributes.hasOwnProperty('stone_15_sku') ? recipe.extended_attributes.stone_15_sku.value.info.valueUsageVendorId : noStone; // eslint-disable-line

        // Dependency Map
        prodArr = [productSku, metalSku, stoneSku]
        stoneSku2 !== noStone ? prodArr.push(stoneSku2) : ''
        stoneSku3 !== noStone ? prodArr.push(stoneSku3) : ''
        stoneSku4 !== noStone ? prodArr.push(stoneSku4) : ''
        stoneSku5 !== noStone ? prodArr.push(stoneSku5) : ''
        stoneSku6 !== noStone ? prodArr.push(stoneSku6) : ''
        stoneSku7 !== noStone ? prodArr.push(stoneSku7) : ''
        stoneSku8 !== noStone ? prodArr.push(stoneSku8) : ''
        stoneSku9 !== noStone ? prodArr.push(stoneSku9) : ''
        stoneSku10 !== noStone ? prodArr.push(stoneSku10) : ''
        stoneSku11 !== noStone ? prodArr.push(stoneSku11) : ''
        stoneSku12 !== noStone ? prodArr.push(stoneSku12) : ''
        stoneSku13 !== noStone ? prodArr.push(stoneSku13) : ''
        stoneSku14 !== noStone ? prodArr.push(stoneSku14) : ''
        stoneSku15 !== noStone ? prodArr.push(stoneSku15) : ''

        // Stone Map
        stoneArr = [stoneSku]
        stoneSku2 !== noStone ? stoneArr.push(stoneSku2) : ''
        stoneSku3 !== noStone ? stoneArr.push(stoneSku3) : ''
        stoneSku4 !== noStone ? stoneArr.push(stoneSku4) : ''
        stoneSku5 !== noStone ? stoneArr.push(stoneSku5) : ''
        stoneSku6 !== noStone ? stoneArr.push(stoneSku6) : ''
        stoneSku7 !== noStone ? stoneArr.push(stoneSku7) : ''
        stoneSku8 !== noStone ? stoneArr.push(stoneSku8) : ''
        stoneSku9 !== noStone ? stoneArr.push(stoneSku9) : ''
        stoneSku10 !== noStone ? stoneArr.push(stoneSku10) : ''
        stoneSku11 !== noStone ? stoneArr.push(stoneSku11) : ''
        stoneSku12 !== noStone ? stoneArr.push(stoneSku12) : ''
        stoneSku13 !== noStone ? stoneArr.push(stoneSku13) : ''
        stoneSku14 !== noStone ? stoneArr.push(stoneSku14) : ''
        stoneSku15 !== noStone ? stoneArr.push(stoneSku15) : ''
    }

    fcMeta.metal = metalSku
    fcMeta.stones = stoneArr

    let pidsObj
    let childProducts = []
    let fluidConfigurePrice
    let fluidConfigureImageURL
    let fluidConfigureRecipeID
    let fluidConfigureResourceURL
    let pid
    pid = demandwareProductID
    fluidConfigurePrice = recipe.prices.USD
    fluidConfigureImageURL = recipe.images.view_1.split(',')[0] + ',0.20.png'
    fluidConfigureRecipeID = recipe.id
    fluidConfigureResourceURL = recipe.uri + '?api-key=' + apiKey

    let form = {
        pid: pid,
        pidsObj: pidsObj,
        childProducts: childProducts,
        quantity: quantity,
        fluidConfigurePrice: fluidConfigurePrice,
        fluidConfigureImageURL: fluidConfigureImageURL,
        fluidConfigureRecipeID: fluidConfigureRecipeID,
        fluidConfigureResourceURL: fluidConfigureResourceURL,
        format: 'ajax',
        fluidProductDependense: JSON.stringify(prodArr),
        fluidConfigureQuantity: quantity,
        fluidConfigureMetalSKU: metalSku,
        fluidConfigureStones: JSON.stringify(stoneArr),
        fluidConfigureMetadata: JSON.stringify(fcMeta),
        fluidConfigureProductID: fluidConfigureProductID,
        hasPhoto: hasPhoto,
        productName: productName,
        colorbarCategory: getColorBarCategory(fluidConfigureProductID),
        finishedsku: recipe?.custom?.finishedSKU,
        comingFromColorbar: true
    }
    return form
}

export const isLimitReached = (productId, basket) => {
    return !!basket?.productItems?.find(
        (item) => item?.productId === productId && !!item?.c_productCartLimitReached
    )
}

export const getProductLineItemUrl = (basket, variant) => {
    const item = basket?.productItems?.find((i) => i.itemId === variant?.itemId) || variant

    const baseUrl = variant?.slugUrl
    const params = new URLSearchParams()
    let isExternalRedirection = false
    // existing fluid configure params
    if (
        item?.c_fluidConfigureProductID &&
        item?.c_fluidConfigureImageURL &&
        item?.c_fluidConfigureRecipeID
    ) {
        params.set('product', item.c_fluidConfigureProductID)
        params.set('customer', '1613')
        params.set('recipe', item.c_fluidConfigureRecipeID)
        isExternalRedirection = true
    }

    // additional drive recipe params
    if (item?.c_driveRecipeID && item?.c_hasEngraving) {
        params.set('Quantity', '1')
        params.set('uuid', item.itemId)
        params.set('driveRecipeID', item.c_driveRecipeID)
    }

    const url = params.toString().length > 0 ? `${baseUrl}?${params.toString()}` : baseUrl

    return formattedURL(url, isExternalRedirection)
}
